import type { RefObject } from 'react'

import { useLayoutEffect, useState } from 'react'
import { computeMenuLeft } from './lib'

type Params = {
  opened: boolean
  triggerRef: RefObject<HTMLElement | null>
  listRef: RefObject<HTMLDivElement | null>
  menuOffset?: number
}

const DEFAULT_MENU_OFFSET = 8
const POSITION_EPSILON = 0.5

type Position = {
  left: number
  top: number
}

export const useContentActionMenuShift = ({
  opened,
  triggerRef,
  listRef,
  menuOffset = DEFAULT_MENU_OFFSET,
}: Params) => {
  const [position, setPosition] = useState<Position>({
    left: 0,
    top: 0,
  })

  useLayoutEffect(() => {
    if (!opened || !listRef.current) return

    const updateMenuPosition = () => {
      const triggerElement = triggerRef.current
      const listElement = listRef.current

      if (!triggerElement || !listElement) return

      const buttonRect = triggerElement.getBoundingClientRect()
      const menuRect = listElement.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const menuWidth = menuRect.width
      const left = computeMenuLeft({
        buttonRect,
        menuWidth,
        viewportWidth,
      })
      const top = buttonRect.bottom + menuOffset

      setPosition((prevPosition) =>
        Math.abs(left - prevPosition.left) > POSITION_EPSILON ||
        Math.abs(top - prevPosition.top) > POSITION_EPSILON
          ? { left, top }
          : prevPosition
      )
    }

    updateMenuPosition()

    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)

    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [menuOffset, opened])

  return position
}
